import { useState } from 'react'
import { supabase } from '../lib/supabase'
import { useI18n } from '../i18n/useI18n'
import { useAuth } from '../auth/useAuth'
import { useAsync } from '../lib/useAsync'
import { formatDate, formatMoney } from '../lib/format'
import { canEnterData } from '../lib/types'
import type { Lease, Tenant, Unit } from '../lib/types'
import { Loading, ErrorState, EmptyState, Banner } from '../components/States'
import { DocumentLink } from '../components/DocumentViewer'
import { Occupancy } from '../components/Occupancy'
import { UnitSheet } from './UnitSheet'
import { LeaseSheet } from './LeaseSheet'
import { LeaseContractSheet } from './LeaseContractSheet'

interface LeaseRow extends Lease {
  tenant: { full_name: string; phone: string | null } | null
}

interface Payload {
  unit: Unit
  leases: LeaseRow[]
  tenants: Tenant[]
  units: Unit[]
}

async function load(unitId: string): Promise<Payload> {
  const { data: unit, error: uErr } = await supabase
    .from('units')
    .select('id, name, kind, floor, notes, vacant_since, vacancy_reason')
    .eq('id', unitId)
    .single()
  if (uErr) throw uErr

  // Historique complet de l'unite : tous les baux, du plus recent au
  // plus ancien. C'est ce qui rend une augmentation de loyer visible.
  const { data: leases, error: lErr } = await supabase
    .from('leases')
    .select(
      'id, unit_id, tenant_id, rent_ht, tva_rate, rent_ttc, deposit_amount,' +
        ' advance_month, agency_fee, start_date, end_date, status,' +
        ' contract_document, supersedes_lease_id, change_reason,' +
        ' a_verifier, verifier_note, tenant:tenants(full_name, phone)',
    )
    .eq('unit_id', unitId)
    .order('start_date', { ascending: false, nullsFirst: false })
  if (lErr) throw lErr

  const { data: tenants } = await supabase
    .from('tenants')
    .select('id, full_name, phone, id_type, id_number, notes')
    .order('full_name')

  const { data: units } = await supabase
    .from('units')
    .select('id, name, kind, floor, notes, vacant_since, vacancy_reason')
    .order('name')

  return {
    unit: unit as Unit,
    leases: (leases ?? []) as unknown as LeaseRow[],
    tenants: (tenants as Tenant[]) ?? [],
    units: (units as Unit[]) ?? [],
  }
}

export function UnitDetailScreen({
  unitId,
  onBack,
}: {
  unitId: string
  onBack: () => void
}) {
  const { t } = useI18n()
  const { profile } = useAuth()
  const { data, loading, error, reload } = useAsync(() => load(unitId), [unitId])
  const [sheet, setSheet] = useState<
    'closed' | 'unit' | 'lease' | { change: Lease } | { contract: LeaseRow }
  >('closed')

  const peutSaisir = canEnterData(profile?.role ?? null)

  if (loading) {
    return (
      <div className="screen">
        <BackLink onBack={onBack} label={t('back')} />
        <Loading />
      </div>
    )
  }
  if (error || !data) {
    return (
      <div className="screen">
        <BackLink onBack={onBack} label={t('back')} />
        <ErrorState message={error} onRetry={reload} />
      </div>
    )
  }

  const { unit, leases, tenants, units } = data
  const today = new Date()
  const courant = leases.find((l) => l.status === 'actif' || l.status === 'expire')
  const historique = leases.filter((l) => l.id !== courant?.id)

  const finCourant = courant?.end_date ? new Date(courant.end_date) : null
  const expire = finCourant !== null && finCourant < today
  const jours = finCourant
    ? Math.round((finCourant.getTime() - today.getTime()) / 86400000)
    : null

  return (
    <div className="screen">
      <BackLink onBack={onBack} label={t('back')} />

      <div className="screen__head">
        {/* Nom issu de la base, jamais traduit. */}
        <h1 className="screen__title">{unit.name}</h1>
        {peutSaisir && (
          <button
            type="button"
            className="btn btn--ghost btn--small"
            onClick={() => setSheet('unit')}
          >
            {t('edit')}
          </button>
        )}
      </div>

      <p className="muted">
        {unit.kind === 'commercial'
          ? t('unit_kind_commercial')
          : t('unit_kind_habitation')}
        {unit.floor ? ` · ${unit.floor}` : ''}
      </p>

      {expire && (
        <Banner tone="error">
          <strong>{t('lease_expired_since', { n: Math.abs(jours ?? 0) })}</strong>
        </Banner>
      )}
      {!expire && jours !== null && jours <= 60 && (
        <Banner tone="warn">
          <strong>{t('lease_expires_in', { n: jours })}</strong>
        </Banner>
      )}

      {unit.vacant_since && (
        <Banner tone="warn">
          <strong>
            {t('unit_vacant_since', { date: formatDate(unit.vacant_since) })}
          </strong>
          {unit.vacancy_reason && <span>{unit.vacancy_reason}</span>}
          <span>{t('unit_vacancy_hint')}</span>
        </Banner>
      )}

      <h2 className="screen__section">{t('unit_current_lease')}</h2>
      {courant ? (
        <LeaseCard
          lease={courant}
          onChangeRent={
            peutSaisir ? () => setSheet({ change: courant }) : undefined
          }
          onAddContract={
            peutSaisir ? () => setSheet({ contract: courant }) : undefined
          }
        />
      ) : (
        <EmptyState
          title={t('unit_no_lease')}
          action={
            peutSaisir ? (
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => setSheet('lease')}
              >
                {t('lease_add')}
              </button>
            ) : undefined
          }
        />
      )}

      {courant && peutSaisir && (
        <button
          type="button"
          className="btn btn--ghost"
          onClick={() => setSheet('lease')}
        >
          {t('lease_add')}
        </button>
      )}

      {historique.length > 0 && (
        <>
          <h2 className="screen__section">{t('unit_history')}</h2>
          <div className="stack">
            {historique.map((l) => (
              <LeaseCard
                key={l.id}
                lease={l}
                onAddContract={
                  peutSaisir ? () => setSheet({ contract: l }) : undefined
                }
              />
            ))}
          </div>
        </>
      )}

      {sheet === 'unit' && (
        <UnitSheet
          unit={unit}
          onClose={() => setSheet('closed')}
          onSaved={() => {
            setSheet('closed')
            reload()
          }}
        />
      )}

      {sheet === 'lease' && (
        <LeaseSheet
          unit={unit}
          units={units}
          tenants={tenants}
          supersedes={null}
          onClose={() => setSheet('closed')}
          onSaved={(cree) => {
            reload()
            // On enchaine sur les pieces du bail : un contrat fait
            // plusieurs pages et c'est maintenant qu'on les a en main.
            // Sans cela il fallait savoir que l'ecran existait.
            if (cree.leaseId) {
              setSheet({
                contract: {
                  id: cree.leaseId,
                  unit_id: unit.id,
                  tenant_id: cree.tenantId,
                  contract_document: null,
                } as LeaseRow,
              })
            } else {
              setSheet('closed')
            }
          }}
        />
      )}

      {typeof sheet === 'object' && 'change' in sheet && (
        <LeaseSheet
          unit={unit}
          units={units}
          tenants={tenants}
          supersedes={sheet.change}
          onClose={() => setSheet("closed")}
          onSaved={() => {
            setSheet("closed")
            reload()
          }}
        />
      )}

      {typeof sheet === 'object' && 'contract' in sheet && (
        <LeaseContractSheet
          leaseId={sheet.contract.id}
          unitId={sheet.contract.unit_id}
          tenantId={sheet.contract.tenant_id}
          currentDocumentId={sheet.contract.contract_document}
          onClose={() => setSheet('closed')}
          onSaved={() => {
            setSheet('closed')
            reload()
          }}
        />
      )}
    </div>
  )
}

function BackLink({ onBack, label }: { onBack: () => void; label: string }) {
  return (
    <button type="button" className="backlink" onClick={onBack}>
      ← {label}
    </button>
  )
}

function LeaseCard({
  lease,
  onChangeRent,
  onAddContract,
}: {
  lease: LeaseRow
  onChangeRent?: () => void
  onAddContract?: () => void
}) {
  const { t } = useI18n()
  const taux = Number(lease.tva_rate)

  return (
    <div className="card">
      <div className="card__row">
        <span className="list__name">{lease.tenant?.full_name ?? ''}</span>
        <span className={`chip chip--${lease.status === 'actif' ? 'ok' : 'warn'}`}>
          {lease.status === 'actif'
            ? t('lease_status_actif')
            : lease.status === 'expire'
              ? t('lease_status_expire')
              : t('lease_status_termine')}
        </span>
      </div>

      <div className="card__figure card__figure--small">
        {formatMoney(lease.rent_ttc)}
      </div>

      {taux > 0 && (
        <p className="muted">
          {t('lease_ht')} {formatMoney(lease.rent_ht)} · {t('lease_tva_amount')}{' '}
          {formatMoney(lease.rent_ttc - lease.rent_ht)}
        </p>
      )}

      {/* Duree d'occupation de ce bail. Sur la fiche d'une unite on
          mesure bail par bail : l'historique est juste dessous, les
          durees s'additionnent a l'oeil. */}
      <Occupancy startDate={lease.start_date} endDate={lease.end_date} />

      <dl className="deflist">
        <dt>{t('lease_start')}</dt>
        <dd>{lease.start_date ? formatDate(lease.start_date) : '—'}</dd>
        <dt>{t('lease_end')}</dt>
        <dd>{lease.end_date ? formatDate(lease.end_date) : '—'}</dd>
        {lease.deposit_amount > 0 && (
          <>
            <dt>{t('lease_deposit')}</dt>
            <dd>{formatMoney(lease.deposit_amount)}</dd>
          </>
        )}
        {lease.advance_month > 0 && (
          <>
            <dt>{t('lease_advance')}</dt>
            <dd>{formatMoney(lease.advance_month)}</dd>
          </>
        )}
        {lease.tenant?.phone && (
          <>
            <dt>{t('tenant_phone')}</dt>
            <dd>{lease.tenant.phone}</dd>
          </>
        )}
      </dl>

      {lease.change_reason && (
        <p className="muted">
          <strong>{t('lease_change_reason')} :</strong> {lease.change_reason}
        </p>
      )}

      {/* Tout champ sans source documentaire est signale plutot que
          comble par une valeur inventee. */}
      {lease.a_verifier && lease.verifier_note && (
        <div className="banner banner--warn">
          {/* Pas d'etiquette « A verifier » : la note dit elle-meme ce
              qui manque, l'annoncer en plus n'ajoutait rien. */}
          <span>{lease.verifier_note}</span>
        </div>
      )}

      {/* Le contrat de ce bail : consultable, et joignable s'il
          manque. Un bail se saisit souvent avant que le contrat ne
          revienne signe des deux parties. */}
      {lease.contract_document ? (
        <DocumentLink
          documentId={lease.contract_document}
          label={t('lease_contract')}
        />
      ) : (
        <span className="chip chip--warn">{t('lease_contract_missing')}</span>
      )}

      {onAddContract && (
        <button
          type="button"
          className="btn btn--ghost btn--small"
          onClick={onAddContract}
        >
          {/* « Remplacer » n'a plus de sens : un bail porte plusieurs
              pieces, on les gere, on n'en echange pas une. */}
          {lease.contract_document
            ? t('lease_contract_manage')
            : t('lease_contract_add')}
        </button>
      )}

      {onChangeRent && (
        <button type="button" className="btn btn--ghost btn--small" onClick={onChangeRent}>
          {t('lease_change_rent')}
        </button>
      )}
    </div>
  )
}
